import React from 'react';
import { motion } from 'framer-motion';
import { useForm, ValidationError } from '@formspree/react';
import { SectionLabel, SectionHeading } from './ui/UI';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
const focus = ['Attack-path reporting', 'AI app validation', 'Fix verification', 'Evidence packs', 'Self-serve onboarding', 'SaaS-style pricing', 'MSSP workflows', 'Internal runner demand', 'Customer security review use cases'];
const ideal = ['B2B SaaS companies', 'AI-native companies', 'Security teams preparing for customer reviews', 'Companies approaching SOC 2 or cyber-insurance renewal', 'MSSPs and vCISOs', 'Teams launching AI applications', 'Fortune 10,000 business units needing faster validation'];
export function EarlyAccess() {
  const [state, handleSubmit] = useForm('mykvgpkn');
  const {
    submitting,
    succeeded
  } = state;
  return <section id="early-access" className="relative w-full py-32 border-t border-white/5" data-id="element-256">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-257">
        <SectionLabel number="20" label="Early Access" className="mb-8" data-id="element-258" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-259">
          <div className="lg:col-span-7" data-id="element-260">
            <SectionHeading size="xl" data-id="element-261">
              Periscan is being
              <br data-id="element-262" />
              <span className="text-white/40" data-id="element-263">reborn in 2026.</span>
            </SectionHeading>
          </div>
          <div className="lg:col-span-5 lg:pt-4" data-id="element-264">
            <p className="text-lg text-white/60 leading-relaxed" data-id="element-265">
              We are opening Periscan to select early access teams before
              general availability. Limited spots — prioritizing teams with
              active validation, customer review, AI security, remediation, or
              evidence needs.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12" data-id="element-266">
          {/* Form */}
          <div className="lg:col-span-7 order-2 lg:order-1" data-id="element-267">
            {succeeded ? <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="rounded-2xl bg-gradient-to-br from-[#1a8fd4]/10 to-[#0a0d12] border border-[#1a8fd4]/30 p-12 text-center" data-id="element-268">
                <CheckCircle2 className="w-16 h-16 text-[#1a8fd4] mx-auto mb-6" data-id="element-269" />
                <h3 className="text-2xl font-semibold text-white mb-2" data-id="element-270">
                  You're on the list.
                </h3>
                <p className="text-base text-white/60" data-id="element-271">
                  We'll be in touch as early access opens.
                </p>
              </motion.div> : <form onSubmit={handleSubmit} className="rounded-2xl bg-[#070a0e] border border-white/5 p-8 md:p-10" data-id="element-272">
                <p className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-6" data-id="element-273">
                  Request Early Access
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-id="element-274">
                  <Field id="email" name="email" type="email" label="Work email *" required placeholder="you@company.com" data-id="element-275" />
                  <Field id="name" name="name" label="Name" placeholder="Jane Doe" data-id="element-276" />
                  <Field id="company" name="company" label="Company" placeholder="Your company" data-id="element-277" />
                  <Field id="website" name="website" label="Company website" placeholder="company.com" data-id="element-278" />
                  <Field id="role" name="role" label="Role" placeholder="e.g. CISO, Security Engineer" data-id="element-279" />
                  <SelectField id="company_size" name="company_size" label="Company size" options={['1–10', '11–50', '51–200', '201–1,000', '1,001–10,000', '10,000+']} data-id="element-280" />
                </div>

                <div className="mb-4" data-id="element-281">
                  <SelectField id="use_case" name="use_case" label="Primary use case" options={['Attack-path report', 'AI app validation', 'Fix verification', 'Customer security review evidence', 'SOC 2 / ISO readiness', 'Cyber-insurance readiness', 'MSSP / client reporting', 'Internal validation', 'Business unit validation', 'Other']} data-id="element-282" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-id="element-283">
                  <SelectField id="plan" name="plan" label="Interested plan" options={['Snapshot', 'Team', 'Business', 'Scale', 'MSSP', 'Enterprise', 'Not sure yet']} data-id="element-284" />
                  <Field id="stack" name="stack" label="Cloud / identity stack" placeholder="e.g. AWS + Okta" data-id="element-285" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-id="element-286">
                  <SelectField id="ai_app" name="ai_app" label="AI app to validate?" options={['Yes', 'No', 'Maybe']} data-id="element-287" />
                  <SelectField id="mssp" name="mssp" label="MSSP or vCISO?" options={['Yes', 'No']} data-id="element-288" />
                </div>

                <div className="mb-6" data-id="element-289">
                  <label htmlFor="prompted" className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-2" data-id="element-290">
                    What prompted your interest?
                  </label>
                  <textarea id="prompted" name="prompted" rows={3} placeholder="Customer review, AI launch, SOC 2 prep, cyber-insurance renewal..." className="w-full bg-[#050608] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#1a8fd4]/50 focus:ring-1 focus:ring-[#1a8fd4]/50 transition-all resize-none" data-id="element-291" />
                </div>

                <ValidationError errors={state.errors} className="text-sm text-red-400 mb-4" data-id="element-292" />

                <button type="submit" disabled={submitting} className="group w-full flex items-center justify-between gap-2 px-6 py-4 bg-white text-black font-medium rounded-full hover:bg-white/90 transition-colors disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-293">
                  <span data-id="element-294">
                    {submitting ? 'Requesting…' : 'Request Early Access'}
                  </span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" data-id="element-295" />
                </button>

                <p className="mt-4 text-xs text-white/40 text-center" data-id="element-296">
                  Early access is limited. We prioritize teams with active
                  validation, customer review, AI security, or evidence needs.
                </p>
              </form>}
          </div>

          {/* Sidebar info */}
          <div className="lg:col-span-5 order-1 lg:order-2 space-y-8" data-id="element-297">
            <div data-id="element-298">
              <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-4" data-id="element-299">
                Focus areas
              </p>
              <div className="space-y-2" data-id="element-300">
                {focus.map((f, i) => <div key={i} className="flex items-center gap-3 py-1.5 border-b border-white/5 last:border-0" data-id="element-301">
                    <span className="font-mono text-[10px] text-[#1a8fd4]/60" data-id="element-302">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-sm text-white/70" data-id="element-303">{f}</span>
                  </div>)}
              </div>
            </div>

            <div data-id="element-304">
              <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-4" data-id="element-305">
                Ideal early access teams
              </p>
              <div className="flex flex-wrap gap-1.5" data-id="element-306">
                {ideal.map((t, i) => <span key={i} className="px-2.5 py-1 text-xs text-white/60 bg-white/[0.03] border border-white/5 rounded-md" data-id="element-307">
                    {t}
                  </span>)}
              </div>
            </div>

            <div className="p-6 rounded-xl bg-gradient-to-br from-[#1a8fd4]/10 to-transparent border border-[#1a8fd4]/20" data-id="element-308">
              <p className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-3" data-id="element-309">
                Availability
              </p>
              <p className="text-sm text-white/70 leading-relaxed" data-id="element-310">
                Periscan is in pre-release. The first release focuses on
                attack-path reports, AI app validation reports, fix
                verification, and evidence-ready reports. Additional
                capabilities will roll out in later beta phases.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function Field({
  id,
  name,
  label,
  type = 'text',
  placeholder,
  required
}: {
  id: string;
  name: string;
  label: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
}) {
  return <div data-id="element-311">
      <label htmlFor={id} className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-2" data-id="element-312">
        {label}
      </label>
      <input id={id} name={name} type={type} required={required} placeholder={placeholder} className="w-full bg-[#050608] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#1a8fd4]/50 focus:ring-1 focus:ring-[#1a8fd4]/50 transition-all" data-id="element-313" />
    </div>;
}
function SelectField({
  id,
  name,
  label,
  options
}: {
  id: string;
  name: string;
  label: string;
  options: string[];
}) {
  return <div data-id="element-314">
      <label htmlFor={id} className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-2" data-id="element-315">
        {label}
      </label>
      <select id={id} name={name} className="w-full bg-[#050608] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#1a8fd4]/50 focus:ring-1 focus:ring-[#1a8fd4]/50 transition-all appearance-none" data-id="element-316">
        <option value="" data-id="element-317">Select…</option>
        {options.map(o => <option key={o} value={o} data-id="element-318">
            {o}
          </option>)}
      </select>
    </div>;
}